import type { Doc, DocCollection } from '@blocksuite/store';
import { posthog } from '@pique/core/posthog';
import { WorkspaceFlavour } from '@pique/env/workspace';
import type {
  Workspace,
  WorkspaceMetadata,
  WorkspaceProfileInfo,
  WorkspacesService,
} from '@toeverything/infra';
import { DocsService } from '@toeverything/infra';
import { applyUpdate, encodeStateAsUpdate } from 'yjs';

import {
  TranscriptionService,
  WorkspacePropertiesAdapter,
} from '../../properties';

export async function findOrCreateNamedCloudWorkspace(
  workspacesService: WorkspacesService,
  workspaceName: string
): Promise<WorkspaceMetadata> {
  await workspacesService.list.waitForRevalidation();
  const workspaces = workspacesService.list.workspaces$.value;
  const cloudWorkspaces = workspaces.filter(
    (w: WorkspaceMetadata) => w.flavour === WorkspaceFlavour.PIQUE_CLOUD
  );

  for (const ws of cloudWorkspaces) {
    const profile = workspacesService.getProfile(ws);
    profile.revalidate();
    const profileInfo = await profile.profile$.waitFor(
      (p: WorkspaceProfileInfo | null) => p?.name != null
    );
    if (profileInfo?.name === workspaceName) {
      return ws;
    }
  }

  return workspacesService.create(
    WorkspaceFlavour.PIQUE_CLOUD,
    async (docCollection: DocCollection) => {
      docCollection.meta.setName(workspaceName);
    }
  );
}

export interface CloudWorkspaceReadyInfo {
  rootReadyTimedOut: boolean;
  metaWasMissing: boolean;
  metaDocsCount: number;
  metaHasVersion: boolean;
  metaWorkspaceVersion?: number;
  metaPageVersion?: number;
  metaBlockVersionsCount: number;
}

export async function ensureCloudWorkspaceReadyForDocCreation(
  cloudWorkspace: Workspace,
  timeoutMs = 3000
): Promise<CloudWorkspaceReadyInfo> {
  const meta = cloudWorkspace.docCollection.meta;
  const metaDocsBefore = meta.docs;
  const metaWasMissing = !Array.isArray(metaDocsBefore);
  const metaDocsCount = Array.isArray(metaDocsBefore)
    ? metaDocsBefore.length
    : 0;
  const metaHasVersion = meta.hasVersion;
  const metaWorkspaceVersion = meta.workspaceVersion;
  const metaPageVersion = meta.pageVersion;
  const metaBlockVersionsCount = meta.blockVersions
    ? Object.keys(meta.blockVersions).length
    : 0;

  const rootDocReadyPromise = cloudWorkspace.engine.waitForRootDocReady();
  const timeoutPromise = new Promise<'timeout'>(resolve =>
    setTimeout(() => resolve('timeout'), timeoutMs)
  );

  const readyResult = await Promise.race([rootDocReadyPromise, timeoutPromise]);
  const rootReadyTimedOut = readyResult === 'timeout';

  if (rootReadyTimedOut) {
    cloudWorkspace.engine.doc.markAsReady(cloudWorkspace.id);
  }

  cloudWorkspace.docCollection.meta.initialize();
  return {
    rootReadyTimedOut,
    metaWasMissing,
    metaDocsCount,
    metaHasVersion,
    metaWorkspaceVersion,
    metaPageVersion,
    metaBlockVersionsCount,
  };
}

export function syncDocContent(sourceDoc: Doc, cloudDoc: Doc): void {
  if (!sourceDoc.ready) {
    sourceDoc.load();
  }
  if (!cloudDoc.ready) {
    cloudDoc.load();
  }

  const sourceSpaceDoc = sourceDoc.spaceDoc;
  const cloudSpaceDoc = cloudDoc.spaceDoc;

  if (!sourceSpaceDoc) {
    posthog.captureException(
      new Error('syncDocContent: source spaceDoc missing'),
      {
        context: 'syncDocContent',
        sourceDocId: sourceDoc.id,
        cloudDocId: cloudDoc.id,
        errorType: 'MissingSourceSpaceDoc',
        sourceReady: sourceDoc.ready,
      }
    );
    throw new Error('Failed to share. Please try again.');
  }

  if (!cloudSpaceDoc) {
    posthog.captureException(
      new Error('syncDocContent: cloud spaceDoc missing'),
      {
        context: 'syncDocContent',
        sourceDocId: sourceDoc.id,
        cloudDocId: cloudDoc.id,
        errorType: 'MissingCloudSpaceDoc',
        cloudReady: cloudDoc.ready,
      }
    );
    throw new Error('Failed to share. Please try again.');
  }

  try {
    const update = encodeStateAsUpdate(sourceSpaceDoc);
    if (!update || update.length === 0) {
      posthog.captureException(new Error('syncDocContent: empty update'), {
        context: 'syncDocContent',
        sourceDocId: sourceDoc.id,
        cloudDocId: cloudDoc.id,
        errorType: 'EmptyUpdate',
      });
      throw new Error('Failed to share. Please try again.');
    }
    applyUpdate(cloudSpaceDoc, update);
  } catch (err) {
    posthog.captureException(err, {
      context: 'syncDocContent',
      sourceDocId: sourceDoc.id,
      cloudDocId: cloudDoc.id,
      errorType:
        err instanceof RangeError
          ? 'RangeError'
          : (err?.constructor?.name ?? 'Unknown'),
      errorMessage: err instanceof Error ? err.message : String(err),
      errorStack: err instanceof Error ? err.stack : undefined,
      sourceReady: sourceDoc.ready,
      cloudReady: cloudDoc.ready,
    });

    throw new Error('Failed to share. Please try again.');
  }
}

export async function copyPageProperties(
  sourceWorkspace: Workspace,
  cloudWorkspace: Workspace,
  sourceDocId: string,
  cloudDocId: string,
  docTitle: string
): Promise<void> {
  const sourcePropertiesAdapter = sourceWorkspace.scope.get(
    WorkspacePropertiesAdapter
  );
  const cloudPropertiesAdapter = cloudWorkspace.scope.get(
    WorkspacePropertiesAdapter
  );
  const sourceTranscription = sourceWorkspace.scope.get(TranscriptionService);
  const cloudTranscription = cloudWorkspace.scope.get(TranscriptionService);

  sourcePropertiesAdapter.ensurePageProperties(sourceDocId);
  cloudPropertiesAdapter.ensurePageProperties(cloudDocId);

  cloudPropertiesAdapter.transact(() => {
    const srcItems =
      sourcePropertiesAdapter.getPageActionItems(sourceDocId) || [];
    if (Array.isArray(srcItems) && srcItems.length > 0) {
      const copied = srcItems.map(item => ({
        ...item,
        id: crypto.randomUUID(),
        pageId: cloudDocId,
        pageTitle: docTitle,
      }));
      cloudPropertiesAdapter.setPageActionItems(cloudDocId, copied);
    }

    const attendees =
      sourcePropertiesAdapter.getPageAttendees(sourceDocId) || [];
    if (Array.isArray(attendees) && attendees.length > 0) {
      cloudPropertiesAdapter.setPageAttendees(
        cloudDocId,
        attendees.map(a => ({ ...a }))
      );
    }

    const overview = sourcePropertiesAdapter.getPageOverview(sourceDocId);
    if (overview) {
      cloudPropertiesAdapter.setPageOverview(cloudDocId, overview);
    }
  });

  // Copy transcription
  await sourceTranscription.ensurePageLoaded(sourceDocId);
  const transcription =
    sourceTranscription.getPageTranscription(sourceDocId) || [];
  if (Array.isArray(transcription) && transcription.length > 0) {
    cloudTranscription.setPageTranscription(
      cloudDocId,
      transcription.map(c => ({ ...c }))
    );
  }

  // Copy summary
  const summary = sourceTranscription.getPageSummary(sourceDocId) || [];
  if (Array.isArray(summary) && summary.length > 0) {
    cloudTranscription.setPageSummary(cloudDocId, summary.map(s => ({ ...s })));
  }
}

export async function waitForDocSync(
  cloudWorkspace: Workspace,
  cloudDocId: string,
  delayMs = 300
): Promise<() => void> {
  const docsService = cloudWorkspace.scope.get(DocsService);
  const { doc: openedDoc, release } = docsService.open(cloudDocId);

  await openedDoc.waitForSyncReady();
  await cloudWorkspace.engine.waitForDocSynced();
  await new Promise(resolve => setTimeout(resolve, delayMs));

  return release;
}
